import {fadeInAnimation} from 'src/app/animations/fade-in.animation';
import {Logger} from '../../class/helper/logger.class';
import {Loader} from '../../models/loader/loader.redux';
import {cloneDeep} from 'lodash';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {UtilitiesService} from 'src/app/services/utilities-service/utilities.service';
import {ActivatedRoute, Params} from '@angular/router';
import {IRecordEmit} from '../../interfaces/record';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {NzMarks} from 'ng-zorro-antd/slider';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IRecordHistoryMyExperience, RecordHistoryExperienceAction, RecordHistoryMyExperienceState} from '../../models/record-history-my-experience';
import {StorageService} from '../../services/storage/storage.service';
import {IUser} from '../../models/auth';
import {ServiceAction, ServiceState} from '../../models/service';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-my-experience',
  templateUrl: './record-history-my-experience.component.html',
  styleUrls: ['./record-history-my-experience.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryMyExperienceComponent implements OnInit, OnDestroy {
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(RecordHistoryMyExperienceState.getWasDeployed);

  @Input() recordHistoryMyExperience: IRecordHistoryMyExperience;
  @Output() recordHistoryMyExperienceChange = new EventEmitter<IRecordEmit>();

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;
  disableForm: boolean;
  loadInfo: boolean;

  wasDeployedSubscription: Subscription;

  skeletonTemplate: any[] = [];

  form: UntypedFormGroup;

  marks: NzMarks = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10'
  };

  marksAdherenceToPlan: NzMarks = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10'
  };

  marksEasyOrDifficult: NzMarks = {
    0: 'Imposible',
    2: '',
    4: '',
    6: '',
    8: '',
    10: 'Excelente'
  };

  marksWellness: NzMarks = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10'
  };
  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService,
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordHistoryMyExperience) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
  }

  async initValuesHttp(): Promise<void> {
    try {
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordHistoryExperienceAction.GetActive(this.serviceId, this.patientId)));
      this.recordHistoryMyExperience = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryMyExperienceState.getRecordHistoryMyExperience)));
      this.recordHistoryMyExperienceChange.emit({ record: this.recordHistoryMyExperience, updated: false });
      this.setFormProperties(this.recordHistoryMyExperience);
      this.loadInfo = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  emitChangeUpdate(): void {
    this.recordHistoryMyExperience.updatedAt = new Date().toISOString();
    this.recordHistoryMyExperienceChange.emit({ record: this.recordHistoryMyExperience, updated: true });
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();
    if (this.form.invalid) {
      return this.messageAlert.createMessage('warning', 'Ingresa todos los campos que son requeridos');
    }

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando historial ...')));
      await firstValueFrom(this.store.dispatch(new RecordHistoryExperienceAction.PatchActive(
        this.serviceId, this.patientId, this.recordHistoryMyExperience._id, this.form.getRawValue())
      ));
      this.messageAlert.createMessage('success', 'Mi experiencia  guardada exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar mi experiencia. Intente más tarde', 5000);
      this.recordHistoryMyExperience = cloneDeep(await firstValueFrom(this.store.selectOnce(RecordHistoryMyExperienceState.getRecordHistoryMyExperience)));
      this.setFormProperties(this.recordHistoryMyExperience);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(recordHistoryMyExperience?: IRecordHistoryMyExperience): void {
    this.disableForm = !this.userLogged?.experts;
    this.form = new UntypedFormGroup({
      likedPlan: new UntypedFormControl( recordHistoryMyExperience?.likedPlan ?? 1, []),
      planRatingInPeriod: new UntypedFormControl( recordHistoryMyExperience?.planRatingInPeriod ?? 1, []),
      easyOrDifficultFollowThPlan: new UntypedFormControl( recordHistoryMyExperience?.easyOrDifficultFollowThPlan ?? 0, []),
      feelingOfImprovementInWellBeing: new UntypedFormControl( recordHistoryMyExperience?.feelingOfImprovementInWellBeing ?? 1, []),
      workOnSomeOtherAspect: new UntypedFormControl( recordHistoryMyExperience?.workOnSomeOtherAspect ?? false, []),
      workedAspect: new UntypedFormControl( recordHistoryMyExperience?.workedAspect ?? '', []),
      actionOfTheWorkedAspect: new UntypedFormControl( recordHistoryMyExperience?.actionOfTheWorkedAspect ?? '', []),
    });
    this.onChangeWorkOtherAspect(this.recordHistoryMyExperience.workOnSomeOtherAspect);

    if (this.userLogged.experts || !this.recordHistoryMyExperience.appointmentId) {
      this.form.disable();
    }
  }

  onChangeWorkOtherAspect(value: boolean): void {
    const [workedAspectCtrl, actionOfTheWorkedAspectCtrl] = [this.form.get('workedAspect'), this.form.get('actionOfTheWorkedAspect')];
    if (!value) {
      // Remove validators and clear fields
      workedAspectCtrl.setValue('');
      workedAspectCtrl.clearValidators();
      actionOfTheWorkedAspectCtrl.setValue('');
      actionOfTheWorkedAspectCtrl.clearValidators();
    } else {
      // Add validators
      workedAspectCtrl.addValidators([Validators.required]);
      actionOfTheWorkedAspectCtrl.addValidators([Validators.required]);
    }

    workedAspectCtrl.updateValueAndValidity();
    actionOfTheWorkedAspectCtrl.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.wasDeployedSubscription.unsubscribe();
  }
}
